import { Inject, Injectable, Optional } from '@angular/core';
import { GalaxyDatePipe } from '@vendasta/galaxy/pipes';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, SchedulerLike } from 'rxjs';
import { delay, filter, switchMap, take, tap } from 'rxjs/operators';
import { FEATURE_BULK_ACTIONS_V2 } from '../../flags';
import { ActionListsDetailsStoreService } from '../action-lists-details.store.service';
import { ActionListsApiService } from '../action-lists.api.service';
import { ActionListHistoryItem, ActionListHistoryResponse } from '../action-lists.api.service.response';
import { BulkActionsProgressReporter } from './bulk-actions-reporter.service';

// This enum is not a 1-to-1 representation of possible statuses in an action list history item
// Specifically, RETRYING is a partner-center-client status only
export enum ActionListStatuses {
  NOT_STARTED = 'not-started',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
  RETRYING = 'retrying',
}

export enum ActionListHistoryTypes {
  LIST_IMPORT = 'ImportListHistoryItemModel',
}

export interface ActionListHistoryError {
  reason: string;
  numberOfAccounts: number;
}

export interface ActionListHistory {
  itemId: string;
  title: string;
  description: string;
  numberOfAccounts: string;
  impersonated: boolean;
  performedBy: string;
  performedDate: string;
  type: string;
  actions: string[];
  skippedFile?: string;
  originalFile?: string;
  napDataFile?: string;
  reportUrl?: string;
  tags?: string[];
  errors?: ActionListHistoryError[];
  progressPercentage?: number;
  status: string;
  importListSessionId?: string;
}

@Injectable({ providedIn: 'root' })
export class ActionListsHistoryStoreService {
  private hasMore$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private history$$: BehaviorSubject<ActionListHistory[]> = new BehaviorSubject([]);
  private actionListId$$: BehaviorSubject<string> = new BehaviorSubject('');
  private nextCursor: string | null;
  public history$: Observable<ActionListHistory[]> = this.history$$.asObservable();
  public hasMore$: Observable<boolean> = this.hasMore$$.asObservable();
  private cursor$$: BehaviorSubject<string> = new BehaviorSubject(null);
  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public loading$: Observable<boolean> = this.loading$$.asObservable();

  constructor(
    private actionListsApiService: ActionListsApiService,
    private actionListsDetailsStoreService: ActionListsDetailsStoreService,
    private readonly progress: BulkActionsProgressReporter,
    @Inject(FEATURE_BULK_ACTIONS_V2) private readonly isBulkActionsV2Enabled$: Observable<boolean>,
    @Optional() @Inject('Scheduler') private readonly scheduler?: SchedulerLike,
    @Optional() @Inject('DELAY_TIME') private DELAY_TIME?: number,
  ) {
    this.init();
  }

  private init(): void {
    const DELAY = this.DELAY_TIME || 1000;
    const sched = this.scheduler || undefined;
    combineLatest([this.actionListId$$, this.cursor$$])
      .pipe(
        tap(() => this.loading$$.next(true)),
        delay(DELAY, sched), // letting backend catch up to the new history being created
        filter(([actionListId, _]) => !!actionListId),
        switchMap(([actionListId, cursor]) => {
          return this.actionListsApiService.getActionListHistory(actionListId, cursor);
        }),
      )
      .subscribe(
        (response: ActionListHistoryResponse) => {
          this.handleResponse(response);
          this.loading$$.next(false);
        },
        () => {
          this.loading$$.next(false);
        },
      );
  }

  retryImport(historyItemId: string): void {
    const currentHistoryList = this.history$$.getValue();
    currentHistoryList.forEach((history) => {
      if (history.itemId === historyItemId && history.status !== ActionListStatuses.RETRYING) {
        history.status = ActionListStatuses.RETRYING;
      }
    });
  }

  markImportAsFailed(historyItemId: string): void {
    const currentHistoryList = this.history$$.getValue();
    currentHistoryList.forEach((history) => {
      if (history.itemId === historyItemId) {
        history.status = ActionListStatuses.FAILED;
      }
    });
  }

  loadHistory(actionListId: string): void {
    this.history$$.next([]);
    this.cursor$$.next('');
    this.actionListId$$.next(actionListId);
  }

  loadMore(): void {
    if (this.hasMore$$.getValue()) {
      this.cursor$$.next(this.nextCursor);
    }
  }

  private formatNumberOfAccounts(historyItem: ActionListHistoryItem): string {
    if (historyItem.type === 'BulkCreateBrandHistoryItemModel') {
      return `${historyItem.totalAccounts} Locations Created`;
    }

    if (historyItem.type === 'BulkProductActivationHistoryItemModel') {
      return `${historyItem.totalAccounts} of ${historyItem.totalRows} Order initiated`;
    }

    return `${historyItem.totalAccounts} of ${historyItem.totalRows} Successful`;
  }

  private formatTitle(historyItem: ActionListHistoryItem): string {
    if (historyItem.type === 'PauseCampaignHistoryItemModel' || historyItem.type === 'AddToCampaignHistoryItemModel') {
      return `${historyItem.title} (${historyItem.campaignName})`;
    }
    return `${historyItem.title}`;
  }

  private formatDescription(historyItem: ActionListHistoryItem): string {
    if (historyItem.type === 'AddToCampaignHistoryItemModel') {
      let date = new GalaxyDatePipe().transform(historyItem.scheduledDate, DateFormat.medium);
      date = date + ' (' + Intl.DateTimeFormat().resolvedOptions().timeZone + ')';
      return date;
    }
  }

  private formatErrors(historyItem: ActionListHistoryItem): ActionListHistoryError[] {
    if (historyItem.errors) {
      return Object.keys(historyItem.errors).map((reason) => ({
        reason: reason,
        numberOfAccounts: historyItem.errors[reason],
      }));
    }
    return [];
  }

  private getActions(historyItem: ActionListHistoryItem, errors: ActionListHistoryError[]): string[] {
    const actions: string[] = [];
    switch (historyItem.type) {
      case 'ImportListHistoryItemModel': {
        actions.push('download-original-file');
        if (historyItem.skippedFile) {
          actions.push('download-skipped-file');
        }
        break;
      }
      case 'ExportAccountNAPandProductsHistoryItemModel': {
        if (errors.length > 0) {
          actions.push('view-inline-errors');
        }
        actions.push('download-nap-data-file');
        break;
      }
      case 'AddToCampaignHistoryItemModel': {
        if (errors.length > 0) {
          actions.push('view-inline-errors');
        }
        break;
      }
      case 'BulkCreateBrandHistoryItemModel': {
        if (errors.length > 0) {
          actions.push('view-inline-errors');
        }
        actions.push('view-brand-report');
        break;
      }
      case 'BulkAddTagsHistoryItemModel': {
        actions.push('view-tags');
        break;
      }
      default: {
        break;
      }
    }
    return actions;
  }

  private formatProgressPercentage(historyItem: ActionListHistoryItem): number {
    if (!historyItem.totalRows || historyItem.processedRows > historyItem.totalRows) {
      return 100;
    }
    return Math.round((historyItem.processedRows * 100) / historyItem.totalRows);
  }

  private constructActionListHistory(historyItem: ActionListHistoryItem): ActionListHistory {
    const errors = this.formatErrors(historyItem);
    return {
      itemId: historyItem.itemId,
      title: this.formatTitle(historyItem),
      description: this.formatDescription(historyItem),
      numberOfAccounts: this.formatNumberOfAccounts(historyItem),
      performedBy: historyItem.performedBy,
      impersonated: historyItem.isImpersonated,
      performedDate: historyItem.created,
      type: historyItem.type,
      actions: this.getActions(historyItem, errors),
      skippedFile: historyItem.skippedFile,
      originalFile: historyItem.originalFile,
      napDataFile: historyItem.filename,
      reportUrl: historyItem.reportUrl,
      tags: historyItem.tags,
      errors: errors,
      status: historyItem.status,
      progressPercentage: this.formatProgressPercentage(historyItem),
      importListSessionId: historyItem.importListSessionId,
    };
  }

  private handleResponse(response: ActionListHistoryResponse): void {
    this.hasMore$$.next(Boolean(response.cursor));
    this.nextCursor = response.cursor;
    let history: ActionListHistory[] = [];

    history = this.history$$.getValue();
    history = history.concat(
      response.items.map((historyItem) => {
        const item = this.constructActionListHistory(historyItem);
        this.startPollingHistoryItem(item);
        return item;
      }),
    );
    this.history$$.next(history);
    this.updateHistoryListStatusInStore();
  }

  private constructDownloadUrl(filePath: string): string {
    const fileName = filePath.split('/').slice(-1)[0];
    const encodedFilePath = encodeURIComponent(filePath);
    const encodedFileName = encodeURIComponent(fileName);
    return `/download?filepath=${encodedFilePath}&filename=${encodedFileName}`;
  }

  downloadFile(filePath: string): void {
    const downloadUrl = this.constructDownloadUrl(filePath);
    document.querySelector('#download-frame').setAttribute('src', downloadUrl);
  }

  addErrorAccountsToList(listName: string, historyItemId: string, errorReason: string): Observable<boolean> {
    return this.actionListsApiService.addErrorAccountToList(listName, historyItemId, errorReason).pipe(
      tap(
        () => true,
        () => false,
      ),
    );
  }

  private startPollingHistoryItem(item: ActionListHistory): void {
    if (item.status !== ActionListStatuses.COMPLETED) {
      this.isBulkActionsV2Enabled$
        .pipe(
          switchMap((isEnabled) => (isEnabled ? EMPTY : of(true))),
          delay(5000),
          switchMap(() => {
            return this.actionListsApiService.getHistoryItem(item.itemId).pipe(
              tap((response) => this.handlePollingResponse(response)),
              take(1),
            );
          }),
          take(1),
        )
        .subscribe();
    }
  }

  private handlePollingResponse(historyItem: ActionListHistoryItem): void {
    const updatedItem = this.constructActionListHistory(historyItem);
    this.startPollingHistoryItem(updatedItem);
    this.history$$.next(
      this.history$$.getValue().map((item) => {
        if (item.itemId === updatedItem.itemId) {
          if (item.status === ActionListStatuses.RETRYING && updatedItem.status === ActionListStatuses.FAILED) {
            return item;
          }
          return updatedItem;
        } else {
          return item;
        }
      }),
    );
    this.updateHistoryListStatusInStore();
  }

  private updateHistoryListStatusInStore(): void {
    const inProgressItems = this.history$$.getValue().filter((item) => item.status === ActionListStatuses.IN_PROGRESS);
    const hasInProgressItems = inProgressItems.length > 0;
    this.actionListsDetailsStoreService.signalHistoryInProgress(hasInProgressItems);
    const isBrand = this.actionListId$$.getValue().startsWith('G-');
    if (inProgressItems && !isBrand) {
      this.actionListsDetailsStoreService.initializeStore(this.actionListId$$.getValue());
    }
  }
}
